import { useContext } from 'react'

import {
  Box, Button, Form, ModalContext, SectionDivider, SidebarModal, SidebarNoticeBox,
} from '@domain-group/suit-theme'

import EntitySelector from '@components/EntitySelector'

import timeZones from '@functions/timeZones'

import useCurrentUser from '@hooks/useCurrentUser'

import countryCallingCodes from '@functions/countryCallingCodes'

import ScopedConditionalContent from '@components/ScopedConditionalContent'
import useCreateUserModal from './hooks/useCreateUserModal'

interface CreateUserModalProps {
  callbacks: any,
  modalKey?: string,
  showModal: boolean,
}

const CreateUserModal = (props: CreateUserModalProps) => {
  const { callbacks, modalKey = 'CreateUserModal', showModal } = props
  const { closeModal, createUser } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData?.[modalKey] ?? {}
  const { user } = modalPayload

  const createUserModalPayload = useCreateUserModal(user)

  const {
    callbacks: { createUser: createFn },
    creating,
    entityKey,
    filteredRoles,
    saveEnabled,
    userForm,
  } = createUserModalPayload

  const {
    entityState,
    entityState: {
      association_id,
      country_calling_code,
      email,
      first_name,
      last_name,
      mobile_number,
      password,
      password_confirmation,
      role_id,
      time_zone,
      username,
    },
    errors,
    handlers,
    setEntityState,
  } = userForm

  const userPayload = {
    callbacks: {
      action: createFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions: {
      include_user_association_id: true,
    },
    shouldRedirect: true,
    toastText: 'User Created',
  }
  const { isAdmin } = useCurrentUser()

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title="Create" titleSecondLine="User" />

      <SidebarModal.Content>
        <Form>
          <Form.Field errorMessage={errors.role_id} label="* Role">
            {/* @ts-expect-error */}
            <select name="role_id" value={role_id} {...handlers}>
              <option value="">Please Select...</option>
              {filteredRoles.map(role => (
                <option key={role.id} value={role.id}>
                  {role.title}
                </option>
              ))}
            </select>
          </Form.Field>

          {entityKey && (
            <EntitySelector
              callbacks={{
                selectItem: entity => setEntityState({ association_id: entity ? entity.id : null }),
              }}
              entityKey={entityKey}
              label="* Association"
              marginTop="large"
              selectedItemId={association_id}
            />
          )}

          <Form.Field errorMessage={errors.first_name} label="* First Name" marginTop="large">
            <input name="first_name" type="text" value={first_name} {...handlers} />
          </Form.Field>

          <Form.Field errorMessage={errors.last_name} label="* Last Name" marginTop="large">
            <input name="last_name" type="text" value={last_name} {...handlers} />
          </Form.Field>

          <Form.Field errorMessage={errors.email} label="* Email" marginTop="large">
            <input
              data-validate-field-on="blur"
              name="email"
              type="email"
              value={email}
              {...handlers}
              onBlur={e => setEntityState({ email: e.target.value, username: username || e.target.value })}
            />
          </Form.Field>

          <Form.Field errorMessage={errors.time_zone} label="* Timezone" marginTop="large">
            {/* @ts-expect-error */}
            <select name="time_zone" value={time_zone} {...handlers}>
              <option value="">Please Select...</option>
              {timeZones.map(timezone => (
                <option key={timezone.key} value={timezone.value}>
                  {timezone.title}
                </option>
              ))}
            </select>
          </Form.Field>

          <SectionDivider boxProps={{ marginTop: 'large' }}>ACCOUNT</SectionDivider>

          {!isAdmin && (
            <SidebarNoticeBox boxProps={{ marginBottom: 'none' }}>
              <SidebarNoticeBox.Paragraph boxProps={{ marginTop: 'none' }}>
                Your username will log you in to the system. We recommend your email but it must be
                unique and not already in use.
              </SidebarNoticeBox.Paragraph>
            </SidebarNoticeBox>
          )}

          <Form.Field errorMessage={errors.username} label="* Username" marginTop="large">
            <input name="username" type="text" value={username} {...handlers} />
          </Form.Field>

          <Form.Field boxProps={{ flex: 1 }} label="* Mobile" marginTop="large">
            <Box flexDirection="row" marginRight="small">
              {/* @ts-expect-error */}
              <select
                name="country_calling_code"
                value={country_calling_code}
                {...handlers}
              >
                {countryCallingCodes.map(({ key, value, title }) => (
                  <option key={key} value={value}>
                    {title}
                  </option>
                ))}
              </select>

              <Form.Field boxProps={{ marginLeft: 'small', width: '70%' }} errorMessage={errors.mobile_number}>
                <input type="text" value={mobile_number} name="mobile_number" {...handlers} />
              </Form.Field>
            </Box>
          </Form.Field>

          <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
            <SidebarNoticeBox boxProps={{ marginTop: 'large', marginBottom: 'none' }}>
              <SidebarNoticeBox.Title>Password Requirements</SidebarNoticeBox.Title>
              <SidebarNoticeBox.Paragraph>
                Passwords must be at least 8 characters in length and contain: an upper case letter,
                a lower case letter, a number, and a special character.
              </SidebarNoticeBox.Paragraph>
            </SidebarNoticeBox>
          </ScopedConditionalContent>

          <Form.Field errorMessage={errors.password} label="* Password" marginTop="large">
            <input data-validate-field-on="blur" name="password" type="password" value={password} {...handlers} />
          </Form.Field>

          <Form.Field errorMessage={errors.password_confirmation} label="* Confirm Password" marginTop="large">
            <input name="password_confirmation" type="password" value={password_confirmation} {...handlers} />
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating}
          onClick={() => createUser(userPayload)}
          size="large"
        >
          {saveEnabled ? 'Create User' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}
CreateUserModal.defaultProps = {
  modalKey: 'CreateUserModal',
}

const LazyLoadedModal = (props: CreateUserModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateUserModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
